/* ============================================================
   Dathent — Calendar
   Month + Week views over real scheduled_posts data. Slot click →
   /app/post/:id. URL state via ?view=&date=.
   Source: pages/calendar.jsx + Calendar.html.
   ============================================================ */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../services/api/client.js';
import { Icons as I } from '../components/pack-icons';
import { BrandIcons } from '../components/pack-icons/brands';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEK_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const PLATFORM_FROM_NAME = (raw) => {
  if (!raw) return null;
  const v = String(raw).toLowerCase();
  if (v.includes('insta')) return 'instagram';
  if (v.includes('face')) return 'facebook';
  if (v.includes('linke')) return 'linkedin';
  if (v === 'x' || v.includes('twit')) return 'x';
  if (v.includes('tik')) return 'tiktok';
  if (v.includes('thread')) return 'threads';
  if (v.includes('tele')) return 'telegram';
  return v;
};

const STATUS_TINT = {
  published: { dot: 'var(--success)', bg: 'color-mix(in srgb, var(--success) 14%, var(--bg-elev))' },
  scheduled: { dot: 'var(--accent)', bg: 'var(--bg-elev)' },
  planned: { dot: 'var(--accent)', bg: 'var(--bg-elev)' },
  queued: { dot: 'var(--accent)', bg: 'var(--bg-elev)' },
  live: { dot: 'var(--warning)', bg: 'color-mix(in srgb, var(--warning) 16%, var(--bg-elev))' },
  draft: { dot: 'var(--text-subtle)', bg: 'var(--bg-subtle)' },
  failed: { dot: 'var(--danger)', bg: 'color-mix(in srgb, var(--danger) 14%, var(--bg-elev))' },
};

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function parseDateParam(value) {
  if (!value) return new Date();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? new Date() : d;
}

function formatDateParam(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/* Strip "Generating: " / "Generating N/N: " prefix from prompt-style titles. */
function cleanPromptPrefix(s) {
  if (typeof s !== 'string') return s;
  return s.replace(/^Generating(?:\s+\d+\s*\/\s*\d+)?\s*:\s*/i, '').trim();
}

/* Resolve a human title for a scheduled post — scheduler payload nests
   content as { contentItem: {title, topic}, activeVersion: {hook, body, caption} }. */
function deriveSlotTitle(item) {
  const c = item?.content || {};
  const ci = c.contentItem || {};
  const v = c.activeVersion || {};
  const candidates = [
    v.hook,
    ci.title,
    v.body,
    v.caption,
    ci.topic,
  ];
  for (const raw of candidates) {
    if (typeof raw !== 'string') continue;
    const cleaned = cleanPromptPrefix(raw).split('\n')[0];
    if (cleaned && cleaned.length >= 3) return cleaned.slice(0, 80);
  }
  return 'Untitled post';
}

/* Map a server scheduled_post + targets into an array of slot rows. */
function expandToSlots(items) {
  const slots = [];
  for (const item of items) {
    const post = item?.post || item;
    const when = post?.scheduledAt || post?.scheduled_at;
    if (!when) continue;
    const d = new Date(when);
    if (Number.isNaN(d.getTime())) continue;
    const targets = item?.targets || [];
    const now = new Date();
    const live = Math.abs(d - now) < 60 * 60 * 1000;
    const baseStatus = post?.status || 'scheduled';
    const title = deriveSlotTitle(item);
    const contentId = post.contentItemId || post.content_item_id || item?.content?.contentItem?.id || post.id;
    for (const t of (targets.length ? targets : [{}])) {
      const tStatus = t.status;
      const status = tStatus === 'published'
        ? 'published'
        : tStatus === 'failed'
          ? 'failed'
          : live && (tStatus === 'queued' || tStatus === 'claimed' || tStatus === 'pending')
            ? 'live'
            : (tStatus || baseStatus);
      slots.push({
        id: `${post.id}::${t.id || 'main'}`,
        contentId,
        date: d,
        hour: d.getHours(),
        minute: d.getMinutes(),
        platform: PLATFORM_FROM_NAME(t.platform || post.platform || ''),
        title,
        status,
      });
    }
  }
  return slots;
}

/* ============================================================
   Top bar — period stepper, view toggle, platform filter, new post
   ============================================================ */
function CalBar({ view, setView, cursor, setCursor, platformFilter, setPlatformFilter, onNewPost }) {
  const label = view === 'month'
    ? `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`
    : (() => {
        const start = new Date(cursor);
        const dow = (start.getDay() + 6) % 7;
        start.setDate(start.getDate() - dow);
        const end = new Date(start);
        end.setDate(end.getDate() + 6);
        const sameMonth = start.getMonth() === end.getMonth();
        return sameMonth
          ? `${MONTHS[start.getMonth()]} ${start.getDate()}–${end.getDate()}, ${end.getFullYear()}`
          : `${MONTHS[start.getMonth()].slice(0, 3)} ${start.getDate()} – ${MONTHS[end.getMonth()].slice(0, 3)} ${end.getDate()}, ${end.getFullYear()}`;
      })();

  const step = (dir) => {
    const next = new Date(cursor);
    if (view === 'month') next.setMonth(next.getMonth() + dir);
    else next.setDate(next.getDate() + 7 * dir);
    setCursor(next);
  };

  const platforms = ['all', 'instagram', 'facebook', 'linkedin', 'x', 'tiktok', 'threads', 'telegram'];

  return (
    <div className="ca-bar">
      <div className="ca-bar-l">
        <button className="btn btn-ghost btn-sm" onClick={() => step(-1)} aria-label="Previous" type="button">
          <I.chevLeft size={14} />
        </button>
        <h2 className="ca-period">{label}</h2>
        <button className="btn btn-ghost btn-sm" onClick={() => step(1)} aria-label="Next" type="button">
          <I.chevRight size={14} />
        </button>
        <button className="btn btn-ghost btn-sm" onClick={() => setCursor(new Date())} type="button">
          Today
        </button>
      </div>

      <div className="ca-bar-r">
        <div className="ca-plat-filter">
          {platforms.map((p) => (
            <button
              key={p}
              className={`lib-plat-chip ${platformFilter === p ? 'is-active' : ''}`}
              onClick={() => setPlatformFilter(p)}
              title={p === 'all' ? 'All platforms' : p}
              type="button"
            >
              {p === 'all'
                ? <span style={{ fontSize: 10, padding: '0 4px' }}>All</span>
                : <span
                    className={`ob-net-ic-${p}`}
                    style={{
                      width: 18, height: 18, borderRadius: 4,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
                    }}
                  >
                    {BrandIcons[p]?.({ size: 11 })}
                  </span>}
            </button>
          ))}
        </div>
        <div className="ca-view">
          <button className={view === 'week' ? 'is-active' : ''} onClick={() => setView('week')} type="button">
            Week
          </button>
          <button className={view === 'month' ? 'is-active' : ''} onClick={() => setView('month')} type="button">
            Month
          </button>
        </div>
        <button className="btn btn-primary btn-sm" onClick={onNewPost} type="button">
          <I.plus size={13} /> New post
        </button>
      </div>
    </div>
  );
}

/* ============================================================
   Slot pill (clickable, navigates to PostPreview)
   ============================================================ */
function SlotPill({ slot, compact, onClick }) {
  const tint = STATUS_TINT[slot.status] || STATUS_TINT.scheduled;
  const time = `${String(slot.hour).padStart(2, '0')}:${String(slot.minute).padStart(2, '0')}`;
  return (
    <button
      type="button"
      onClick={() => onClick(slot)}
      className="ca-slot"
      style={{ background: tint.bg }}
      title={`${slot.title} · ${time}`}
    >
      {slot.platform && (
        <span className={`ca-slot-plat ob-net-ic-${slot.platform}`}>
          {BrandIcons[slot.platform]?.({ size: 10 })}
        </span>
      )}
      <span className="ca-slot-time mono">{time}</span>
      {!compact && <span className="ca-slot-title">{slot.title}</span>}
      <span className="ca-slot-dot" style={{ background: tint.dot }} />
    </button>
  );
}

/* ============================================================
   Day inspector — slides in from the right with the day's full post list
   ============================================================ */
function DayInspector({ day, slots, onClose, onOpenPost, onCreate }) {
  if (!day) return null;
  const sorted = [...slots].sort((a, b) => a.hour * 60 + a.minute - (b.hour * 60 + b.minute));
  const statusCounts = sorted.reduce((m, s) => {
    m[s.status] = (m[s.status] || 0) + 1;
    return m;
  }, {});

  // ESC to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      <div className="ca-insp-scrim" onClick={onClose} />
      <aside className="ca-insp" role="dialog" aria-label="Posts for selected day">
        <header className="ca-insp-h">
          <div>
            <div className="eyebrow"><span>Selected day</span></div>
            <h3>{day.toLocaleDateString('en', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</h3>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-soft btn-sm" onClick={() => onCreate(day)} type="button">
              <I.plus size={12} /> New
            </button>
            <button className="btn btn-ghost btn-sm btn-icon" onClick={onClose} type="button" aria-label="Close">
              <I.x size={14} />
            </button>
          </div>
        </header>

        <div className="ca-insp-counts">
          <div className="ca-insp-count-card">
            <strong>{sorted.length}</strong>
            <span>{sorted.length === 1 ? 'post' : 'posts'}</span>
          </div>
          <div className="ca-insp-counts-pills">
            {Object.entries(statusCounts).map(([status, count]) => {
              const tint = STATUS_TINT[status] || STATUS_TINT.scheduled;
              return (
                <span
                  key={status}
                  className="pill"
                  style={{ background: tint.bg, color: tint.dot, borderColor: 'transparent' }}
                >
                  <span className="dot" style={{ background: tint.dot }} />
                  {status} · {count}
                </span>
              );
            })}
          </div>
        </div>

        <div className="ca-insp-list">
          {sorted.length === 0 ? (
            <div className="ca-insp-empty">
              <I.calendar size={20} />
              <span>This day is open. Create a post or choose another day.</span>
            </div>
          ) : (
            sorted.map((s) => {
              const tint = STATUS_TINT[s.status] || STATUS_TINT.scheduled;
              const time = `${String(s.hour).padStart(2, '0')}:${String(s.minute).padStart(2, '0')}`;
              return (
                <button
                  key={s.id}
                  className="ca-insp-post"
                  type="button"
                  onClick={() => onOpenPost(s)}
                  style={{ borderLeftColor: tint.dot }}
                >
                  <div className="ca-insp-post-h">
                    <span className="ca-insp-time mono">{time}</span>
                    {s.platform && (
                      <span
                        className={`ob-net-ic-${s.platform}`}
                        style={{
                          width: 22, height: 22, borderRadius: 5,
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
                        }}
                      >
                        {BrandIcons[s.platform]?.({ size: 12 })}
                      </span>
                    )}
                    <span className="pill" style={{ background: tint.bg, color: tint.dot, borderColor: 'transparent', marginLeft: 'auto' }}>
                      <span className="dot" style={{ background: tint.dot }} />
                      {s.status}
                    </span>
                  </div>
                  <div className="ca-insp-title">{s.title}</div>
                </button>
              );
            })
          )}
        </div>
      </aside>
    </>
  );
}

/* Distribute the day's slot statuses into proportional bar segments. */
function dayStatusBars(slots) {
  if (!slots.length) return [];
  const groups = {};
  for (const s of slots) {
    groups[s.status] = (groups[s.status] || 0) + 1;
  }
  return Object.entries(groups).map(([status, count]) => ({
    status,
    weight: count / slots.length,
  }));
}

/* "3 published · 2 scheduled" type summary. */
function dayStatusSummary(slots) {
  if (!slots.length) return '';
  const groups = {};
  for (const s of slots) {
    groups[s.status] = (groups[s.status] || 0) + 1;
  }
  return Object.entries(groups)
    .sort(([, a], [, b]) => b - a)
    .map(([status, count]) => `${count} ${status}`)
    .join(' · ');
}

const STATUS_LABEL_SHORT = {
  published: 'Published',
  scheduled: 'Scheduled',
  planned: 'Planned',
  queued: 'Queued',
  live: 'Publishing',
  draft: 'Draft',
  failed: 'Failed',
};

/* ============================================================
   Month view
   ============================================================ */
function MonthView({ cursor, slots, onDayClick, selectedDay }) {
  const today = startOfDay(new Date());
  const firstDay = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const lastDay = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
  const startWeekday = (firstDay.getDay() + 6) % 7;
  const totalCells = Math.ceil((startWeekday + lastDay.getDate()) / 7) * 7;

  const slotsByDay = useMemo(() => {
    const m = new Map();
    for (const s of slots) {
      const key = startOfDay(s.date).toDateString();
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(s);
    }
    for (const arr of m.values()) {
      arr.sort((a, b) => a.hour * 60 + a.minute - (b.hour * 60 + b.minute));
    }
    return m;
  }, [slots]);

  const cells = [];
  for (let i = 0; i < totalCells; i++) {
    const dayOffset = i - startWeekday;
    const date = new Date(firstDay);
    date.setDate(1 + dayOffset);
    const inMonth = date.getMonth() === cursor.getMonth();
    const isToday = date.toDateString() === today.toDateString();
    const dayPosts = slotsByDay.get(startOfDay(date).toDateString()) || [];
    cells.push({ date, inMonth, isToday, posts: dayPosts });
  }

  return (
    <div className="ca-month">
      <div className="ca-month-h">
        {WEEK_DAYS.map((d) => <div key={d} className="ca-month-h-d">{d}</div>)}
      </div>
      <div className="ca-month-grid">
        {cells.map((c, i) => {
          const isSelected = selectedDay && c.date.toDateString() === selectedDay.toDateString();
          const hasPosts = c.posts.length > 0;
          const next = c.posts[0] || null;
          const nextTint = next ? (STATUS_TINT[next.status] || STATUS_TINT.scheduled) : null;
          const bars = dayStatusBars(c.posts);
          const summary = dayStatusSummary(c.posts);
          return (
            <div
              key={i}
              className={`ca-day ${!c.inMonth ? 'is-out' : ''} ${c.isToday ? 'is-today' : ''} ${isSelected ? 'is-selected' : ''} ${c.inMonth ? 'is-clickable' : ''}`}
              onClick={() => { if (c.inMonth) onDayClick(c.date); }}
              role={c.inMonth ? 'button' : undefined}
              tabIndex={c.inMonth ? 0 : -1}
              aria-label={`${c.date.toDateString()}${hasPosts ? `, ${c.posts.length} posts` : ', no posts'}`}
              onKeyDown={(e) => {
                if (c.inMonth && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  onDayClick(c.date);
                }
              }}
            >
              <div className="ca-day-h">
                <span className="ca-day-num">{c.date.getDate()}</span>
                {hasPosts && <span className="ca-day-count">{c.posts.length}</span>}
              </div>

              {hasPosts ? (
                <div className="ca-day-overview">
                  {/* Status progress bar */}
                  <div className="ca-day-bar" aria-hidden="true">
                    {bars.map((b) => {
                      const tint = STATUS_TINT[b.status] || STATUS_TINT.scheduled;
                      return (
                        <span
                          key={b.status}
                          style={{ flexGrow: b.weight, background: tint.dot }}
                        />
                      );
                    })}
                  </div>

                  <div className="ca-day-brief">
                    <span className="ca-day-total">
                      {c.posts.length} {c.posts.length === 1 ? 'post' : 'posts'}
                    </span>
                    <span className="ca-day-mix">{summary}</span>
                  </div>

                  {next && (
                    <div
                      className="ca-day-next"
                      style={{ borderLeftColor: nextTint.dot, background: nextTint.bg }}
                    >
                      <div className="ca-day-next-h">
                        <span className="ca-day-next-time mono">
                          {String(next.hour).padStart(2, '0')}:{String(next.minute).padStart(2, '0')}
                        </span>
                        {next.platform && (
                          <span
                            className={`ob-net-ic-${next.platform}`}
                            style={{
                              width: 14, height: 14, borderRadius: 3,
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
                            }}
                          >
                            {BrandIcons[next.platform]?.({ size: 8 })}
                          </span>
                        )}
                        <span className="ca-day-next-status" style={{ color: nextTint.dot }}>
                          {STATUS_LABEL_SHORT[next.status] || next.status}
                        </span>
                      </div>
                      <div className="ca-day-next-title">{next.title}</div>
                      {c.posts.length > 1 && (
                        <div className="ca-day-more">+{c.posts.length - 1} more</div>
                      )}
                    </div>
                  )}
                </div>
              ) : c.inMonth ? (
                <div className="ca-day-empty-state">
                  <span className="ca-day-empty-label">No posts</span>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ============================================================
   Week view
   ============================================================ */
function WeekView({ cursor, slots, onSlotClick }) {
  const today = startOfDay(new Date());
  const start = new Date(cursor);
  const dow = (start.getDay() + 6) % 7;
  start.setDate(start.getDate() - dow);
  start.setHours(0, 0, 0, 0);
  const days = Array.from({ length: 7 }).map((_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
  const hours = [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21];

  const slotsByDay = useMemo(() => {
    const m = new Map();
    for (const s of slots) {
      const key = startOfDay(s.date).toDateString();
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(s);
    }
    return m;
  }, [slots]);

  return (
    <div className="ca-week">
      <div className="ca-week-h">
        <div className="ca-week-h-spacer" />
        {days.map((d, i) => {
          const isToday = d.toDateString() === today.toDateString();
          return (
            <div key={i} className={`ca-week-h-d ${isToday ? 'is-today' : ''}`}>
              <span className="ca-week-h-day">{WEEK_DAYS[i]}</span>
              <span className="ca-week-h-num">{d.getDate()}</span>
            </div>
          );
        })}
      </div>
      <div className="ca-week-body">
        <div className="ca-week-hours">
          {hours.map((h) => (
            <div key={h} className="ca-week-hour">
              <span className="ca-week-hour-l mono">{String(h).padStart(2, '0')}:00</span>
            </div>
          ))}
        </div>
        {days.map((d, di) => {
          const dayPosts = slotsByDay.get(d.toDateString()) || [];
          return (
            <div key={di} className="ca-week-col">
              {hours.map((h) => <div key={h} className="ca-week-cell" />)}
              {dayPosts.map((p) => {
                const top = ((p.hour - 6) + p.minute / 60) * 56; // 56px per hour
                if (top < 0) return null; // skip pre-6am
                const tint = STATUS_TINT[p.status] || STATUS_TINT.scheduled;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => onSlotClick(p)}
                    className={`ca-wk-slot ca-wk-${p.status}`}
                    style={{ top, background: tint.bg, borderLeftColor: tint.dot }}
                  >
                    <div className="ca-wk-slot-h">
                      {p.platform && (
                        <span
                          className={`ca-slot-plat ob-net-ic-${p.platform}`}
                          style={{ width: 16, height: 16 }}
                        >
                          {BrandIcons[p.platform]?.({ size: 10 })}
                        </span>
                      )}
                      <span className="mono" style={{ fontSize: 10, color: 'var(--text-subtle)' }}>
                        {String(p.hour).padStart(2, '0')}:{String(p.minute).padStart(2, '0')}
                      </span>
                    </div>
                    <div className="ca-wk-slot-t">{p.title}</div>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ============================================================
   Top-level Calendar screen
   ============================================================ */
export const Calendar = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const initialView = searchParams.get('view') === 'week' ? 'week' : 'month';
  const initialDate = parseDateParam(searchParams.get('date'));

  const [view, setView] = useState(initialView);
  const [cursor, setCursor] = useState(initialDate);
  const [platformFilter, setPlatformFilter] = useState('all');
  const [calendarItems, setCalendarItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);

  // Persist view + date in URL
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set('view', view);
    next.set('date', formatDateParam(cursor));
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, cursor]);

  // Fetch calendar window when cursor / view changes
  useEffect(() => {
    let cancelled = false;
    let from, to;
    if (view === 'month') {
      from = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
      to = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    } else {
      const start = new Date(cursor);
      const dow = (start.getDay() + 6) % 7;
      start.setDate(start.getDate() - dow);
      start.setHours(0, 0, 0, 0);
      from = start;
      to = new Date(start);
      to.setDate(start.getDate() + 7);
    }
    setLoading(true);
    api.scheduler.getCalendar(from.toISOString(), to.toISOString())
      .then((res) => { if (!cancelled) setCalendarItems(res?.posts || res?.data?.posts || []); })
      .catch(() => { if (!cancelled) setCalendarItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [view, cursor]);

  const allSlots = useMemo(() => expandToSlots(calendarItems), [calendarItems]);
  const slots = useMemo(() => {
    if (platformFilter === 'all') return allSlots;
    return allSlots.filter((s) => s.platform === platformFilter);
  }, [allSlots, platformFilter]);

  const summary = useMemo(() => {
    const total = allSlots.length;
    const scheduled = allSlots.filter((s) => s.status === 'scheduled' || s.status === 'queued' || s.status === 'planned').length;
    const published = allSlots.filter((s) => s.status === 'published').length;
    const upcoming = allSlots
      .filter((s) => s.status === 'scheduled' || s.status === 'queued' || s.status === 'planned')
      .sort((a, b) => a.date - b.date)[0];
    return { total, scheduled, published, upcoming };
  }, [allSlots]);

  const handleSlotClick = (slot) => {
    // Carry the target/platform context so PostPreview renders the right
    // platform chrome even when one content_item targets multiple networks.
    const params = new URLSearchParams();
    if (slot.targetId) params.set('target', slot.targetId);
    if (slot.platform) params.set('platform', slot.platform);
    const qs = params.toString();
    navigate(`/app/post/${encodeURIComponent(slot.contentId)}${qs ? `?${qs}` : ''}`);
  };
  const handleNewPost = () => navigate('/app/create');
  const handleDayClick = (day) => setSelectedDay(day);

  // Slots for the currently-selected day (filtered by active platform)
  const selectedDaySlots = useMemo(() => {
    if (!selectedDay) return [];
    return slots.filter((s) => startOfDay(s.date).toDateString() === selectedDay.toDateString());
  }, [selectedDay, slots]);

  return (
    <div className="ca-root">
      <div className="ca-top">
        <div>
          <h1 className="ca-h">Calendar</h1>
          <p className="ca-sub">Everything published and scheduled. Click a slot to open the post.</p>
        </div>
        <div className="ca-summary">
          <div className="ca-sum">
            <span className="ca-sum-l">Scheduled</span>
            <span className="ca-sum-v">{summary.scheduled}</span>
          </div>
          <div className="ca-sum">
            <span className="ca-sum-l">Published this window</span>
            <span className="ca-sum-v">{summary.published}</span>
          </div>
          {summary.upcoming && (
            <div className="ca-sum">
              <span className="ca-sum-l">Next post</span>
              <span className="ca-sum-v" style={{ fontSize: 13, fontWeight: 500 }}>
                {MONTHS[summary.upcoming.date.getMonth()].slice(0, 3)} {summary.upcoming.date.getDate()} · {String(summary.upcoming.hour).padStart(2, '0')}:{String(summary.upcoming.minute).padStart(2, '0')}
              </span>
            </div>
          )}
        </div>
      </div>

      <CalBar
        view={view}
        setView={setView}
        cursor={cursor}
        setCursor={setCursor}
        platformFilter={platformFilter}
        setPlatformFilter={setPlatformFilter}
        onNewPost={handleNewPost}
      />

      {loading && allSlots.length === 0 ? (
        <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-subtle)' }}>Loading…</div>
      ) : view === 'month' ? (
        <MonthView
          cursor={cursor}
          slots={slots}
          onDayClick={handleDayClick}
          selectedDay={selectedDay}
        />
      ) : (
        <WeekView cursor={cursor} slots={slots} onSlotClick={handleSlotClick} />
      )}

      <DayInspector
        day={selectedDay}
        slots={selectedDaySlots}
        onClose={() => setSelectedDay(null)}
        onOpenPost={(s) => {
          handleSlotClick(s);
          setSelectedDay(null);
        }}
        onCreate={(d) => {
          setSelectedDay(null);
          navigate(`/app/create?date=${formatDateParam(d)}`);
        }}
      />

      <div className="ca-legend">
        <span className="ca-legend-l">Status</span>
        <span className="ca-legend-i"><i style={{ background: 'var(--accent)' }} />Scheduled</span>
        <span className="ca-legend-i"><i style={{ background: 'var(--warning)' }} />Publishing today</span>
        <span className="ca-legend-i"><i style={{ background: 'var(--success)' }} />Published</span>
        <span className="ca-legend-i"><i style={{ background: 'var(--danger)' }} />Failed</span>
      </div>
    </div>
  );
};

export default Calendar;
